import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { Box, Collapse } from '@mui/material';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Text } from 'components/styleguide';
import { QwireBadge } from 'components/common/QwireBadge';
import { useRootTranslation } from 'utils/hooks';
import CueContainers from './CueContainers';

/** A single clearance rendered as a colored block with the clearance name in a pill.
 * Rights use the primary color, Options use the secondary color, matching the
 * pills shown on the licensor rows (RightsColumn). */
const ClearanceBlock = ({
  clearance,
  isOption,
  cueContainers,
  cueContainersLoading,
  readOnly,
  trackId,
  qTrackId,
  refetch,
}) => {
  const pillSx = isOption
    ? { backgroundColor: 'secondary.300', color: 'secondary.main' }
    : { backgroundColor: 'primary.300', color: 'primary.main' };
  const blockBg = isOption ? 'secondary.300' : 'primary.300';

  return (
    <Box sx={{ mr: 3.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <QwireBadge sx={{ ...pillSx, width: 'fit-content', borderRadius: '0.5rem' }} data-testid="usage-preset-badge">
          {clearance.name}
        </QwireBadge>
      </Box>
      <Box
        sx={{
          width: '100%',
          backgroundColor: blockBg,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'other.divider',
          p: 1.5,
          mb: 2.5,
        }}
      >
        <CueContainers
          cueContainers={clearance.containers}
          allCueContainers={cueContainers}
          cueContainersLoading={cueContainersLoading}
          readOnly={readOnly}
          clearanceId={clearance.id}
          trackId={trackId}
          qTrackId={qTrackId}
          refetch={refetch}
        />
      </Box>
    </Box>
  );
};

const Clearances = ({
  rights = [],
  options = [],
  cueContainers,
  cueContainersLoading,
  readOnly,
  trackId,
  qTrackId,
  refetch,
}) => {
  const t = useRootTranslation('drawers.clearanceUsesAndDurations');
  const [optionsOpen, setOptionsOpen] = useState(false);

  const renderBlock = (clearance, isOption) => (
    <ClearanceBlock
      key={clearance.id}
      clearance={clearance}
      isOption={isOption}
      cueContainers={cueContainers}
      cueContainersLoading={cueContainersLoading}
      readOnly={readOnly}
      trackId={trackId}
      qTrackId={qTrackId}
      refetch={refetch}
    />
  );

  return (
    <Grid item xs={12}>
      {rights.map((clearance) => renderBlock(clearance, false))}

      {options.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setOptionsOpen((open) => !open)}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOptionsOpen((open) => !open)}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mb: 1 }}
            data-testid="uses-for-options-toggle"
          >
            {optionsOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
            <Text bold>{t('usesForOptions')}</Text>
          </Box>
          <Collapse in={optionsOpen} unmountOnExit>
            {options.map((clearance) => renderBlock(clearance, true))}
          </Collapse>
        </Box>
      )}
    </Grid>
  );
};

export default Clearances;
